import React, { useState, useEffect, useRef } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { MessageCircle, Copy, Code, Check, Bot, Image as ImageIcon, X, Loader2, Lock, UserCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { uploadImage } from "@/utils/storage";
import { Trash } from 'lucide-react';
import { canUseAIChat } from "@/utils/planLimits";
import { UpgradePrompt } from "@/components/shared/UpgradePrompt";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const activeAdmins = new Set<string>();

type ChatUser = {
  user_name: string;
  last_message: string;
  last_message_time: string;
  has_unread: boolean;
  unread_count: number;
  assigned_to: string | null;
  visitor_email: string;
  session_id: string | null;
};

type Message = {
  id: string;
  content: string;
  is_admin: boolean;
  created_at: string;
  user_name: string;
  read: boolean;
  image_url: string | null;
};

interface ChatManagementProps {
  companyId: string;
  userId: string;
  userName: string;
  isPlanAllowed: boolean;
  companyPlan: string;
}

export const AIChatSupport = ({ companyId, initialEnabled = false, companyPlan = 'free' }: { companyId: string, initialEnabled?: boolean, companyPlan?: string }) => {
  const [aiEnabled, setAiEnabled] = React.useState(initialEnabled);
  const [isLoading, setIsLoading] = React.useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = React.useState(false);
  const [upgradeMessage, setUpgradeMessage] = React.useState("");
  const { toast } = useToast();
  
  const aiCheck = canUseAIChat(companyPlan);
  const canUseFeature = aiCheck.allowed;

  React.useEffect(() => {
    const fetchAiStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('companies')
          .select('ai_support_enabled')
          .eq('id', companyId)
          .single();
          
        if (error) {
          console.error('Error fetching AI status:', error);
          return;
        }
        
        if (data) {
          setAiEnabled(data.ai_support_enabled || false);
        }
      } catch (err) {
        console.error('Error in fetchAiStatus:', err);
      }
    };
    
    if (companyId) {
      fetchAiStatus();
    }
  }, [companyId]);

  const toggleAISupport = async (enabled: boolean) => {
    // Check if plan allows AI chat
    if (!canUseFeature) {
      setUpgradeMessage(aiCheck.message || "");
      setShowUpgradePrompt(true);
      return;
    }

    try {
      setIsLoading(true);
      
      const { error } = await supabase
        .from('companies')
        .update({ ai_support_enabled: enabled })
        .eq('id', companyId);
        
      if (error) {
        throw error;
      }
      
      setAiEnabled(enabled);
      toast({
        title: 'AI Support ' + (enabled ? 'Enabled' : 'Disabled'),
        description: enabled 
          ? 'AI will now automatically respond to customer messages based on your knowledge base'
          : 'AI responses have been turned off',
      });
    } catch (error) {
      console.error('Error toggling AI support:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update AI support settings',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-between py-2 px-3 bg-muted/30 rounded-lg mb-4">
      <div className="flex items-center space-x-2">
        <Bot className="h-5 w-5 text-primary" />
        <div>
          <h4 className="font-medium">AI Chat Support</h4>
          <p className="text-sm text-muted-foreground">
            {!canUseFeature
              ? 'Available on Starter and Enterprise plans'
              : aiEnabled 
              ? 'AI is responding to chats based on your knowledge base'
              : 'Enable AI to respond based on your knowledge base'
            }
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {!canUseFeature && (
          <Badge variant="outline" className="bg-muted border-muted-foreground/20">
            Premium
          </Badge>
        )}
        {aiEnabled && canUseFeature && (
          <Badge variant="outline" className="bg-primary/10 border-primary/20">
            Active
          </Badge>
        )}
        <Switch
          checked={aiEnabled && canUseFeature}
          onCheckedChange={toggleAISupport}
          disabled={isLoading || !canUseFeature}
        />
      </div>
      
      <UpgradePrompt
        open={showUpgradePrompt}
        onOpenChange={setShowUpgradePrompt}
        message={upgradeMessage}
      />
    </div>
  );
};

export function ChatManagement({ companyId, userId, userName, isPlanAllowed, companyPlan }: ChatManagementProps) {
  // State definitions - keeping all hooks at the top level
  const [chatUsers, setChatUsers] = useState<ChatUser[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentChat, setCurrentChat] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);
  const [notificationsPermission, setNotificationsPermission] = useState<NotificationPermission | null>(null);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const notificationSound = useRef<HTMLAudioElement | null>(null);
  const windowFocused = useRef<boolean>(true);
  const [aiSupportEnabled, setAiSupportEnabled] = useState(false);
  const [showEmbedDialog, setShowEmbedDialog] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#4f46e5');
  const [inboxView, setInboxView] = useState<'all' | 'mine'>('all');
  const [teamMembers, setTeamMembers] = useState<Array<{ id: string; name: string; email: string }>>([]);

  // Fetch team members
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        console.log('Fetching team members for company:', companyId);

        // Fetch all active team members with name field
        const { data: teamMembersData, error: teamError } = await supabase
          .from('team_members')
          .select('user_id, email, name')
          .eq('company_id', companyId)
          .eq('status', 'active');

        if (teamError) {
          console.error('Error fetching team members:', teamError);
          return;
        }

        console.log('Raw team members data:', teamMembersData);

        if (!teamMembersData || teamMembersData.length === 0) {
          console.log('No team members found');
          setTeamMembers([]);
          return;
        }

        // Map team members to a consistent format using the name field
        const members = teamMembersData
          .filter(tm => tm.user_id) // Only include team members with user_id
          .map(tm => ({
            id: tm.user_id,
            name: tm.name || tm.email || 'Team Member',
            email: tm.email || ''
          }));

        // Remove duplicates based on user_id
        const uniqueMembers = Array.from(
          new Map(members.map(m => [m.id, m])).values()
        );

        console.log('Final team members list:', uniqueMembers);
        setTeamMembers(uniqueMembers);
      } catch (error) {
        console.error('Error in fetchTeamMembers:', error);
      }
    };

    if (companyId) {
      fetchTeamMembers();
    }
  }, [companyId]);

  // Register active admin
  useEffect(() => {
    if (userId) {
      activeAdmins.add(userId);

      return () => {
        activeAdmins.delete(userId);
      };
    }
  }, [userId]);

  // Fetch chat users and setup real-time subscription
  useEffect(() => {
    fetchChatUsers();
  }, [companyId, inboxView]);

  // Setup real-time subscription
  useEffect(() => {
    
    const channel = supabase
      .channel('chat_messages')
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'chat_messages',
        filter: `company_id=eq.${companyId}`
      }, (payload) => {
        const newMessage = payload.new as Message;
        
        if (newMessage.is_admin) {
          return;
        }

        if (currentChat === newMessage.user_name) {
          setMessages(prev => [...prev, newMessage]);
          markConversationAsRead(newMessage.user_name);
        } else {
          // Refetch chat users to get updated assignee info
          fetchChatUsers();
          showNotification(newMessage.user_name, newMessage.content);
        }
      })
      .subscribe();

    notificationSound.current = new Audio('/notification-sound.mp3');
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [companyId, currentChat]);

  // Add function to update assignee
  const updateAssignee = async (visitorEmail: string, sessionId: string | null, newAssigneeId: string | null) => {
    try {
      if (sessionId) {
        // Update existing session
        const { error } = await supabase
          .from('chat_sessions')
          .update({ assigned_to: newAssigneeId })
          .eq('id', sessionId);

        if (error) throw error;
      } else {
        // Create new session
        const { error } = await supabase
          .from('chat_sessions')
          .insert({
            company_id: companyId,
            visitor_email: visitorEmail,
            visitor_name: chatUsers.find(u => u.visitor_email === visitorEmail)?.user_name || '',
            assigned_to: newAssigneeId
          });

        if (error) throw error;
      }

      // Update local state
      setChatUsers(prev =>
        prev.map(user =>
          user.visitor_email === visitorEmail
            ? { ...user, assigned_to: newAssigneeId }
            : user
        )
      );

      const assigneeName = teamMembers.find(m => m.id === newAssigneeId)?.name || 'Unassigned';
      toast.success(`Chat assigned to ${assigneeName}`);
    } catch (error) {
      console.error('Error updating assignee:', error);
      toast.error('Failed to update assignee');
    }
  };

  // Setup notifications and window focus events
  useEffect(() => {
    requestNotificationPermission();
    
    const handleFocus = () => {
      windowFocused.current = true;
    };
    
    const handleBlur = () => {
      windowFocused.current = false;
    };
    
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Fetch AI support status
  useEffect(() => {
    const fetchAISupport = async () => {
      try {
        const { data, error } = await supabase
          .from('companies')
          .select('ai_support_enabled')
          .eq('id', companyId)
          .single();
          
        if (error) {
          console.error('Error fetching AI support:', error);
          return;
        }
        
        if (data) {
          setAiSupportEnabled(data.ai_support_enabled || false);
        }
      } catch (err) {
        console.error('Error in fetchAISupport:', err);
      }
    };
    
    if (companyId) {
      fetchAISupport();
    }
  }, [companyId]);

  const showNotification = (userName: string, message: string) => {
    try {
      if (windowFocused.current) {
        return;
      }

      if (notificationSound.current) {
        notificationSound.current.play().catch(err => {
          console.error("Failed to play notification sound:", err);
        });
      }

      if (Notification.permission === 'granted') {
        const notification = new Notification(`New message from ${userName}`, {
          body: message,
          icon: '/favicon.ico'
        });
        
        notification.onclick = () => {
          window.focus();
          selectChat(userName);
          notification.close();
        };
      }
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  };

  const requestNotificationPermission = async () => {
    try {
      if (!('Notification' in window)) {
        console.log('This browser does not support desktop notifications');
        return;
      }

      const permission = await Notification.requestPermission();
      setNotificationsPermission(permission);
      setShowNotificationPrompt(false);
      
      if (permission === 'default') {
        setShowNotificationPrompt(true);
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    }
  };

  const fetchChatUsers = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("chat_messages")
        .select("user_name, email, content, created_at, is_admin, read")
        .eq("company_id", companyId)
        .eq("is_admin", false)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching chat users:", error);
        return;
      }

      // Fetch chat sessions to get assignee info
      const { data: sessions, error: sessionsError } = await supabase
        .from("chat_sessions")
        .select("id, visitor_email, assigned_to")
        .eq("company_id", companyId);

      if (sessionsError) {
        console.error("Error fetching chat sessions:", sessionsError);
      }

      // Create a map of email -> session for quick lookup
      const sessionsMap = new Map(
        (sessions || []).map(s => [s.visitor_email, s])
      );

      const userMap = new Map<string, ChatUser>();

      data.forEach(message => {
        if (!userMap.has(message.user_name)) {
          const unreadMessages = data.filter(msg =>
            msg.user_name === message.user_name && !msg.read && !msg.is_admin
          );

          const session = sessionsMap.get(message.email);

          userMap.set(message.user_name, {
            user_name: message.user_name,
            visitor_email: message.email,
            last_message: message.content,
            last_message_time: message.created_at,
            has_unread: unreadMessages.length > 0,
            unread_count: unreadMessages.length,
            assigned_to: session?.assigned_to || null,
            session_id: session?.id || null
          });
        }
      });

      let sortedUsers = Array.from(userMap.values()).sort((a, b) => {
        return new Date(b.last_message_time).getTime() - new Date(a.last_message_time).getTime();
      });

      console.log('All chats before filtering:', sortedUsers.length);
      console.log('Current inbox view:', inboxView);
      console.log('Current user ID:', userId);
      console.log('Chats with assignees:', sortedUsers.map(u => ({ name: u.user_name, assigned_to: u.assigned_to })));

      // Filter by inbox view
      if (inboxView === 'mine') {
        console.log('Filtering for My Inbox...');
        sortedUsers = sortedUsers.filter(user => {
          const matches = user.assigned_to === userId;
          console.log(`Chat ${user.user_name}: assigned_to=${user.assigned_to}, matches=${matches}`);
          return matches;
        });
        console.log('Chats after filtering for My Inbox:', sortedUsers.length);
      }

      setChatUsers(sortedUsers);
    } catch (error) {
      console.error("Error in fetchChatUsers:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectChat = async (userName: string) => {
    setCurrentChat(userName);
    setMessages([]);
    
    try {
      const { data, error } = await supabase
        .from("chat_messages")
        .select("*")
        .eq("company_id", companyId)
        .eq("user_name", userName)
        .order("created_at", { ascending: true });
      
      if (error) {
        console.error("Error fetching chat messages:", error);
        return;
      }
      
      setMessages(data || []);
      
      markConversationAsRead(userName);
      
    } catch (error) {
      console.error("Error selecting chat:", error);
    }
  };

  const sendMessage = async () => {
    if ((!messageInput.trim() && !selectedImage) || !currentChat) return;
    
    try {
      setIsUploading(selectedImage !== null);
      
      let imageUrl: string | undefined;
      if (selectedImage) {
        try {
          imageUrl = await uploadImage(selectedImage, 'chat-images');
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
          toast("Failed to upload image. Please try again.");
          setIsUploading(false);
          return;
        }
      }
      
      const newMessage = {
        company_id: companyId,
        user_id: userId,
        user_name: currentChat,
        content: messageInput.trim() || (imageUrl ? 'Sent an image' : ''),
        is_admin: true,
        read: true,
        image_url: imageUrl
      };
      
      const { error } = await supabase
        .from("chat_messages")
        .insert(newMessage);
      
      if (error) {
        console.error("Error sending message:", error);
        return;
      }
      
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        content: messageInput.trim() || (imageUrl ? 'Sent an image' : ''),
        is_admin: true,
        created_at: new Date().toISOString(),
        user_name: currentChat,
        read: true,
        image_url: imageUrl
      }]);
      
      setMessageInput('');
      setSelectedImage(null);
      setIsUploading(false);
      
    } catch (error) {
      console.error("Error in sendMessage:", error);
      setIsUploading(false);
    }
  };

  const markConversationAsRead = async (userName: string) => {
    try {
      console.log(`Marking messages as read for ${userName}`);
      console.log(`Company ID: ${companyId}`);
      
      const { data: messagesToMark, error: checkError } = await supabase
        .from("chat_messages")
        .select("id, user_name, company_id, read")
        .eq("company_id", companyId)
        .eq("is_admin", false)
        .eq("user_name", userName)
        .eq("read", false);
      
      if (checkError) {
        console.error("Error checking messages to mark as read:", checkError);
        return;
      }
      
      if (!messagesToMark || messagesToMark.length === 0) {
        return;
      }

      const { data: updateResult, error: updateError } = await supabase.rpc('mark_messages_as_read', {
        p_user_name: userName,
        p_company_id: companyId
      });
      
      if (updateError) {
        console.error("Error marking messages as read:", updateError);
        toast("Could not mark messages as read");
        return;
      }
      
      console.log(`Messages marked as read result:`, updateResult);
      
      setChatUsers(prev => 
        prev.map(user => {
          if (user.user_name === userName) {
            return { ...user, has_unread: false, unread_count: 0 };
          }
          return user;
        })
      );

      setMessages(prev => 
        prev.map(msg => ({
          ...msg,
          read: true
        }))
      );
      
      console.log("All messages marked as read in UI");
      
    } catch (error) {
      console.error("Error marking conversation as read:", error);
      toast("Failed to mark messages as read");
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getEmbedCode = () => {
    return `<script>
  (function() {
    var script = document.createElement('script');
    script.src = 'https://feednatic.com/widget.js';
    script.onload = function() {
      initChatWidget({
        companyId: '${companyId}',
        companyName: '${userName}',
        primaryColor: '${selectedColor}',
        textColor: '#ffffff'
      });
    };
    document.body.appendChild(script);
  })();
</script>`;
  };

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(getEmbedCode());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast("Embed code copied to clipboard");
  };

  const handleImageSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast("Please select an image file");
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast("Image must be less than 5MB");
        return;
      }
      
      setSelectedImage(file);
    }
  };

  const removeSelectedImage = () => {
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const deleteChat = async (userName: string) => {
    try {
      const { error } = await supabase
        .from("chat_messages")
        .delete()
        .eq("company_id", companyId)
        .eq("user_name", userName);

      if (error) {
        throw error;
      }

      setChatUsers(prev => prev.filter(user => user.user_name !== userName));
      if (currentChat === userName) {
        setCurrentChat(null);
        setMessages([]);
      }
      
      toast("Chat deleted successfully");
    } catch (error) {
      console.error("Error deleting chat:", error);
      toast.error("Failed to delete chat");
    }
  };

  // If plan doesn't allow chat feature
  if (!isPlanAllowed) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold tracking-tight">Live Chat</h2>
        </div>
        <Separator className="my-4" />
        <Alert className="bg-muted border-primary/20">
          <Lock className="h-5 w-5 text-primary" />
          <AlertTitle className="text-lg font-semibold mb-2">Feature Not Available</AlertTitle>
          <AlertDescription className="space-y-4">
            <p>Live chat is only available on the Starter and Enterprise plans. You are currently on the {companyPlan.charAt(0).toUpperCase() + companyPlan.slice(1)} plan.</p>
            <Button
              onClick={() => {
                window.location.href = '/admin?tab=billing';
              }}
              variant="default"
            >
              Upgrade Now
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold tracking-tight">Live Chat</h2>
        <Button 
          variant="outline" 
          onClick={() => setShowEmbedDialog(true)}
          className="flex gap-2"
        >
          <Code className="h-4 w-4" />
          Embed Chat
        </Button>
      </div>
      
      <Separator className="my-4" />

      {notificationsPermission === 'denied' && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            Notifications are blocked. Please enable them in your browser settings to receive chat alerts.
          </AlertDescription>
        </Alert>
      )}

      {showNotificationPrompt && (
        <Alert className="mb-4">
          <AlertDescription className="flex justify-between items-center">
            <span>Would you like to receive desktop notifications for new chat messages?</span>
            <Button 
              size="sm" 
              onClick={requestNotificationPermission}
            >
              Enable Notifications
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <AIChatSupport companyId={companyId} initialEnabled={aiSupportEnabled} companyPlan={companyPlan} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-muted p-4 space-y-3">
            <h3 className="text-md font-medium">Conversations</h3>
            <Tabs value={inboxView} onValueChange={(value) => {
              console.log('Tab changed to:', value);
              setInboxView(value as 'all' | 'mine');
            }} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="all">All Chats</TabsTrigger>
                <TabsTrigger value="mine">My Inbox</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="divide-y max-h-[600px] overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-muted-foreground">Loading conversations...</div>
            ) : chatUsers.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">No conversations yet</div>
            ) : (
              chatUsers.map((user) => (
                <div
                  key={user.user_name}
                  className={`p-4 hover:bg-muted/50 ${currentChat === user.user_name ? 'bg-muted/50' : ''}`}
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div
                        className="flex items-center space-x-3 flex-1 cursor-pointer"
                        onClick={() => selectChat(user.user_name)}
                      >
                        <Avatar>
                          <AvatarFallback>{user.user_name.substring(0, 2).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">{user.user_name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {user.last_message}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end space-y-1">
                        <div className="text-xs text-muted-foreground">
                          {new Date(user.last_message_time).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-2">
                          {user.has_unread && (
                            <Badge variant="default" className="text-xs">
                              {user.unread_count}
                            </Badge>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 hover:bg-destructive/10 hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteChat(user.user_name);
                            }}
                          >
                            <Trash className="h-4 w-4" />
                            <span className="sr-only">Delete chat</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <UserCircle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <Select
                        value={user.assigned_to || "unassigned"}
                        onValueChange={(value) => {
                          const newAssigneeId = value === "unassigned" ? null : value;
                          updateAssignee(user.visitor_email, user.session_id, newAssigneeId);
                        }}
                      >
                        <SelectTrigger className="h-8 text-xs flex-1">
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {teamMembers.map((member) => (
                            <SelectItem key={member.id} value={member.id}>
                              {member.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="col-span-1 md:col-span-2 border rounded-lg overflow-hidden flex flex-col h-[600px]">
          {currentChat ? (
            <>
              <div className="bg-muted p-4 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Avatar>
                    <AvatarFallback>{currentChat.substring(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-md font-medium">{currentChat}</h3>
                    {(() => {
                      const currentUser = chatUsers.find(u => u.user_name === currentChat);
                      const assignee = currentUser?.assigned_to;
                      const assigneeName = assignee ? teamMembers.find(m => m.id === assignee)?.name : null;
                      return assigneeName ? (
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <UserCircle className="h-3 w-3" />
                          Assigned to {assigneeName}
                        </div>
                      ) : (
                        <div className="text-xs text-muted-foreground">Unassigned</div>
                      );
                    })()}
                  </div>
                </div>
              </div>
              
              <div className="flex-1 p-4 overflow-y-auto flex flex-col space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center text-muted-foreground my-auto">No messages yet</div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.is_admin ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] px-4 py-2 rounded-lg ${
                          message.is_admin
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        {message.image_url && (
                          <div className="mb-2">
                            <img 
                              src={message.image_url} 
                              alt="Shared image" 
                              className="rounded max-w-full max-h-[200px] object-contain cursor-pointer"
                              onClick={() => window.open(message.image_url, '_blank')}
                            />
                          </div>
                        )}
                        {message.content && (
                          <div className="text-sm">{message.content}</div>
                        )}
                        <div className="text-xs mt-1 opacity-70">
                          {formatTimestamp(message.created_at)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              <div className="p-4 border-t">
                {selectedImage && (
                  <div className="mb-3 relative bg-muted/30 rounded-md p-2">
                    <div className="flex items-center gap-2">
                      <div className="h-14 w-14 rounded overflow-hidden bg-muted flex-shrink-0">
                        <img 
                          src={URL.createObjectURL(selectedImage)} 
                          alt="Selected image" 
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="flex-1 truncate">{selectedImage.name}</div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 flex-shrink-0"
                        onClick={removeSelectedImage}
                        disabled={isUploading}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
                <div className="flex space-x-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={isUploading}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={handleImageSelect}
                    disabled={isUploading}
                  >
                    <ImageIcon className="h-4 w-4" />
                  </Button>
                  <Input
                    type="text"
                    placeholder={selectedImage ? "Add a message (optional)" : "Type your message..."}
                    className="flex-1"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && !isUploading) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    disabled={isUploading}
                  />
                  <Button 
                    onClick={sendMessage} 
                    disabled={(!messageInput.trim() && !selectedImage) || isUploading}
                  >
                    {isUploading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : null}
                    Send
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <MessageCircle className="h-12 w-12 mb-4 opacity-20" />
              <p>Select a conversation to start chatting</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={showEmbedDialog} onOpenChange={setShowEmbedDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Embed Chat Widget</DialogTitle>
            <DialogDescription>
              Choose a color and copy the embed code for your website. Paste it right before the closing body tag.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="widget-color">Widget Color</Label>
              <Input 
                id="widget-color" 
                type="color" 
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value)}
                className="h-10 w-full cursor-pointer"
              />
            </div>
            
            <div className="mt-4">
              <Label>Preview</Label>
              <div className="h-16 flex items-center justify-end p-4 rounded-md mt-1" style={{ backgroundColor: 'white', border: '1px solid #e5e7eb' }}>
                <div className="rounded-full h-12 w-12 flex items-center justify-center shadow-md" style={{ backgroundColor: selectedColor, color: 'white' }}>
                  <MessageCircle className="h-6 w-6" />
                </div>
              </div>
            </div>
            
            <div className="mt-4">
              <Label>Embed Code</Label>
              <div className="bg-muted p-4 rounded-md overflow-auto max-h-[200px] mt-1">
                <pre className="text-sm whitespace-pre-wrap break-all font-mono">
                  {getEmbedCode()}
                </pre>
              </div>
            </div>
          </div>
          
          <DialogFooter className="sm:justify-start">
            <Button 
              type="button" 
              variant="secondary" 
              onClick={copyEmbedCode} 
              className="flex gap-2"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied" : "Copy Code"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const getActiveAdmins = () => Array.from(activeAdmins);
